
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { SafeAuthProvider } from "./context/SafeAuthProvider";
import { SettingsProvider } from "./context/SettingsContext";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Settings from "./pages/Settings";
import NotFound from "./pages/NotFound";
import Dashboard from "./pages/Dashboard";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import AppLayout from "./components/layout/AppLayout";
import Journal from "./pages/Journal";
import History from "./pages/History";
import JournalEntryPage from "./pages/JournalEntryPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CookiePolicy from "./pages/CookiePolicy";
import ErrorBoundary from "./components/ErrorBoundary";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Separate error boundary for each provider to ensure isolation
const AuthProviderWithBoundary = ({ children }: { children: React.ReactNode }) => (
  <ErrorBoundary fallback={<div>Auth unavailable - continuing without authentication</div>}>
    <SafeAuthProvider>{children}</SafeAuthProvider>
  </ErrorBoundary>
);

const SettingsProviderWithBoundary = ({ children }: { children: React.ReactNode }) => (
  <ErrorBoundary fallback={<div>Settings unavailable - using defaults</div>}>
    <SettingsProvider>{children}</SettingsProvider>
  </ErrorBoundary>
);

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <TooltipProvider>
          {/* Auth provider wraps everything first */}
          <AuthProviderWithBoundary>
            {/* Settings provider is independent of auth */}
            <SettingsProviderWithBoundary>
              <div id="app" lang="en">
                <Toaster />
                <Sonner />
                {/* AppLayout now wraps the routes and has access to auth context */}
                <AppLayout>
                  <Routes>
                    <Route path="/" element={
                      <ErrorBoundary>
                        <Index />
                      </ErrorBoundary>
                    } />
                    <Route path="/dashboard" element={
                      <ProtectedRoute>
                        <ErrorBoundary>
                          <Dashboard />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route path="/journal" element={
                      <ProtectedRoute>
                        <ErrorBoundary>
                          <Journal />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route path="/journal/:entryId" element={
                      <ProtectedRoute>
                        <ErrorBoundary>
                          <JournalEntryPage />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route path="/history" element={
                      <ProtectedRoute>
                        <ErrorBoundary>
                          <History />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } />
                    {/* Settings route without ProtectedRoute to prevent redirect issues */}
                    <Route path="/settings" element={
                      <ErrorBoundary>
                        <Settings />
                      </ErrorBoundary>
                    } />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/privacy" element={
                      <ErrorBoundary>
                        <PrivacyPolicy />
                      </ErrorBoundary>
                    } />
                    <Route path="/terms" element={
                      <ErrorBoundary>
                        <TermsOfService />
                      </ErrorBoundary>
                    } />
                    <Route path="/cookies" element={
                      <ErrorBoundary>
                        <CookiePolicy />
                      </ErrorBoundary>
                    } />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </AppLayout>
              </div>
            </SettingsProviderWithBoundary>
          </AuthProviderWithBoundary>
        </TooltipProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
